<template>
  <div>
    <div class="vx-col w-full">
      <ul class="heading-ul mb-5">
        <li>
          <router-link to="/setup/leave-management" class="vx-row">
            <feather-icon icon="ArrowLeftIcon" svgClasses="text-dark" />
            <h4>{{ $t("setup.label.CreateLeave") }}</h4>
          </router-link>
        </li>
      </ul>
    </div>
    <ValidationObserver ref="observer">
      <div class="vx-row">
        <vx-card card-border>
          <div class="vx-row">
            <div class="vx-col w-full lg:w-1/3 md:w-1/3 sm:w-full xs:w-full">
              <div class="dropdown-with-label">
                <small class="date-label"
                  >{{ $t("setup.label.UserName") }}
                  <span class="required">*</span></small
                >
                <ValidationProvider
                  rules="required"
                  v-slot="{ errors }"
                  :name="$t('setup.label.UserName')"
                >
                  <v-select
                    label="username"
                    v-model="userId"
                    :options="userList || []"
                    :reduce="(obj) => obj.id"
                    :placeholder="$t('placeholder.label.SelectOne')"
                    :dir="$vs.rtl ? 'rtl' : 'ltr'"
                    class="min-w-200 w-full"
                  >
                  </v-select>
                  <span class="validate-error" style="color: red">{{
                    errors[0]
                  }}</span>
                </ValidationProvider>
              </div>
            </div>
            <div class="vx-col w-full lg:w-1/3 md:w-1/3 sm:w-full xs:w-full">
              <div class="dropdown-with-label">
                <small class="date-label"
                  >{{ $t("setup.label.ShiftName") }}
                  <span class="required">*</span></small
                >
                <ValidationProvider
                  rules="required"
                  v-slot="{ errors }"
                  :name="$t('setup.label.ShiftName')"
                >
                  <v-select
                    label="name"
                    v-model="sessionName"
                    :options="distinctSessions || []"
                    :reduce="(obj) => obj.name"
                    @input="onChangeSessionName"
                    :placeholder="$t('placeholder.label.SelectOne')"
                    :dir="$vs.rtl ? 'rtl' : 'ltr'"
                    class="min-w-200 w-full"
                  >
                  </v-select>
                  <span class="validate-error" style="color: red">{{
                    errors[0]
                  }}</span>
                </ValidationProvider>
              </div>
            </div>
            <div class="vx-col sm:w-full lg:w-1/3 md:w-1/3 xs:w-full">
              <ValidationProvider
                rules="required"
                v-slot="{ errors }"
                :name="'Leave Reason'"
              >
                <vs-input
                  :label="'Leave Reason'"
                  v-model="reason"
                  class="w-full font-semibold mb-2 starlabel"
                />
                <span class="validate-error" style="color: red">{{
                  errors[0]
                }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="w-full">
            <h3 class="mb-2 font-semibold">
              {{ $t("setup.label.LeaveDetails") }}
            </h3>
            <div
              v-for="(entry, index) in leaveEntries"
              :key="index"
              class="vx-row mb-2 items-center"
            >
              <div class="vx-col md:w-1/5 sm:w-full">
                <div class="dropdown-with-label">
                  <small class="date-label"
                    >{{ $t("setup.label.StartDate") }}
                    <span class="required">*</span></small
                  >
                  <Datepicker
                    v-model="entry.startDate"
                    placeholder="Leave Start Date"
                    class="w-full"
                    @input="onStartDateChange(entry, index)"
                  />
                </div>
              </div>
              <div class="vx-col md:w-1/5 sm:w-full">
                <div class="dropdown-with-label">
                  <small class="date-label"
                    >{{ $t("setup.label.EndDate") }}
                    <span class="required">*</span></small
                  >
                  <Datepicker
                    v-model="entry.endDate"
                    placeholder="Leave End Date"
                    class="w-full"
                  />
                </div>
              </div>

              <div class="vx-col md:w-1/5 sm:w-full">
                <div class="dropdown-with-label">
                  <small class="date-label"
                    >{{ $t("setup.label.LeaveType") }}
                    <span class="required">*</span></small
                  >
                  <v-select
                    v-model="entry.leaveType"
                    :options="['Full day', 'Half day']"
                    placeholder="Select Leave Type"
                    class="w-full"
                  ></v-select>
                </div>
              </div>

              <div class="vx-col md:w-1/5 sm:w-full">
                <div class="dropdown-with-label">
                  <small class="date-label"
                    >{{ $t("setup.label.Session") }}
                    <span class="required">*</span></small
                  >
                  <v-select
                    label="sessionType"
                    v-model="entry.session"
                    :options="sessionList || []"
                    placeholder="Select Session"
                    class="w-full"
                    :reduce="(obj) => obj.configId"
                    :disabled="entry.leaveType !== 'Half day'"
                  ></v-select>
                </div>
              </div>

              <div
                v-if="index !== 0"
                class="vx-col md:w-1/5 sm:w-full flex items-center"
              >
                <vs-button
                  color="danger"
                  type="filled"
                  size="small"
                  icon-pack="feather"
                  icon="icon-trash-2"
                  @click="removeLeaveEntry(index)"
                  >{{ $t("setup.label.Remove") }}</vs-button
                >
              </div>
            </div>

            <div class="vx-row mt-4">
              <div class="vx-col w-full flex justify-center">
                <vs-button
                  icon-pack="feather"
                  icon="icon-plus"
                  color="primary"
                  type="filled"
                  @click="addLeaveEntry"
                >
                  {{ $t("setup.label.AddAnotherDate") }}
                </vs-button>
              </div>
            </div>
          </div>
        </vx-card>
      </div>
      <div class="vx-row center">
        <div class="vx-col w-full">
          <vs-button
            color="primary"
            type="border"
            class="mb-2 footerButton-cancel"
            @click="$router.push('/setup/leave-management')"
            >{{ $t("setup.label.Cancel") }}</vs-button
          >
          <vs-button
            class="mr-3 mb-2 footerButton-next"
            @click="createUserAvailability"
            >{{ $t("setup.label.Submit") }}</vs-button
          >
        </div>
      </div>
    </ValidationObserver>
    <validation-popup
      :errorMessage="errorMessage"
      :warningPopup="warningPopup"
      :warningText="warningText"
      @closeWarning="warningPopup = false"
    ></validation-popup>
  </div>
</template>
<script>
import vSelect from "vue-select";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import flatPickr from "vue-flatpickr-component";
import moment from "moment";
import Datepicker from "vuejs-datepicker";
import "flatpickr/dist/flatpickr.css";
import { mapState } from "vuex";
export default {
  data() {
    return {
      userId: "",
      sessionName: "",
      reason: "",
      warningPopup: false,
      userList: [],
      sessionList: [],
      warningText: "",
      errorMessage: "",
      leaveEntries: [
        {
          startDate: null,
          endDate: null,
          leaveType: "",
          session: "",
        },
      ],
    };
  },
  components: {
    ValidationProvider,
    ValidationObserver,
    "v-select": vSelect,
    flatPickr,
    Datepicker,
  },
  computed: {
    ...mapState({
      shiftsList: (state) => state.shiftManagement.shiftsList,
    }),
    distinctSessions() {
      const seenNames = new Set();
      return this.shiftsList.filter((session) => {
        if (seenNames.has(session.name)) {
          return false;
        }
        seenNames.add(session.name);
        return true;
      });
    },
  },
  methods: {
    onStartDateChange(entry, index) {
      this.leaveEntries[index].endDate = entry.startDate;
    },
    addLeaveEntry() {
      this.leaveEntries.push({
        startDate: null,
        endDate: null,
        leaveType: "",
        session: "",
      });
    },
    removeLeaveEntry(index) {
      this.leaveEntries.splice(index, 1);
    },
    async getUsersList() {
      try {
        const res = await this.$store.dispatch(
          "teamMapping/fetchCreateTemplate"
        );
        this.userList = res && res.data && res.data.appUserData;
      } catch (error) {
        throw Error(
          error.message.errors[0].defaultUserMessage ||
            `Api failed with status code: ${error.message.httpStatusCode}`
        );
      }
    },
    onChangeSessionName(sessionName) {
      const matchedSessions = this.shiftsList.filter(
        (e) => e.name === sessionName
      );
      this.sessionList = matchedSessions;
    },
    async createUserAvailability() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) return;
      const availabilities = this.leaveEntries.flatMap((entry) => {
        const matchedSession = this.shiftsList.find(
          (shift) => shift.configId === entry.session
        );
        const sessionType = matchedSession ? matchedSession.sessionType : "";
        let userName = "";
        if (Array.isArray(this.userList)) {
          const userObj = this.userList.find((u) => u.id === this.userId);
          userName = userObj ? userObj.username || userObj.name || "" : "";
        }
        if (entry.leaveType === "Half day") {
          return [
            {
              userId: this.userId,
              userName: userName,
              configId: entry.session,
              recurrence: "weekly",
              sessionDate: moment(entry.startDate).format("YYYY-MM-DD"),
              sessionEndDate: moment(entry.endDate).format("YYYY-MM-DD"),
              sessionType: sessionType,
              isAvailable: false,
              status: "Leave",
              reason: this.reason,
              count: 1,
            },
          ];
        }
        if (entry.leaveType === "Full day") {
          const allSessionsForDay = this.shiftsList.filter(
            (e) => e.name === this.sessionName
          );
          return allSessionsForDay.map((shift) => ({
            userId: this.userId,
            userName: userName,
            configId: shift.configId,
            recurrence: "weekly",
            sessionDate: moment(entry.startDate).format("YYYY-MM-DD"),
            sessionEndDate: moment(entry.endDate).format("YYYY-MM-DD"),
            sessionType: shift.sessionType,
            isAvailable: false,
            status: "Leave",
            reason: this.reason,
            count: 1,
          }));
        }
        return [];
      });
      try {
        let payload = {
          availabilities,
        };
        const res = await this.$store.dispatch(
          "teams/createUserAvailabilityAction",
          payload
        );
        const isEmptyObject =
          res &&
          res.data &&
          Object.keys(res.data).length === 0 &&
          res.data.constructor === Object;

        if (isEmptyObject) {
          this.$swal({
            title: "Success",
            icon: "success",
            text: "Leave Created Successfully!",
            type: "success",
          });
        } else {
          this.$swal({
            title: "Success",
            icon: "success",
            text: "Leave Request Sent for Approval!",
            type: "success",
          });
        }
        this.$router.push(`/setup/leave-management`);
      } catch (e) {
        const errorMessage =
          e &&
          e.message &&
          e.message.defaultUserMessage &&
          e.message.defaultUserMessage.replace(/^Error:\s*/, "");
        this.$swal({
          title: "Error",
          icon: "error",
          text: errorMessage,
          type: "error",
        });
      }
    },
    async getShiftsListMethod() {
      try {
        await this.$store.dispatch("shiftManagement/getShiftsListAction");
      } catch (error) {
        throw Error(
          error.message.errors[0].defaultUserMessage ||
            `Api failed with status code: ${error.message.httpStatusCode}`
        );
      }
    },
    resetForm() {
      this.$refs.observer.reset();
    },
  },
  watch: {},
  async mounted() {
    this.getShiftsListMethod();
    await this.getUsersList();
  },
};
</script>
